/**
 * The file extension to store an uploaded image under.
 *
 * Every image uploader used to take it from the file NAME with
 * `name.split('.').pop() || 'jpg'`. A name with no dot comes back from that
 * whole — it is its own last segment — so a blob fetched from
 * `https://…gstatic.com/images?q=…` was stored as `{timestamp}.images`, and one
 * from `…/_next/image?url=…` as `{timestamp}.image`. The bytes and the content
 * type were fine; only the name was wrong. But the resize extension builds its
 * thumbnail names from the source name, so those objects got
 * `thumbnails/{timestamp}.images_384x384` instead of
 * `thumbnails/{timestamp}_384x384.webp`, and every derived thumbnail URL 404'd.
 *
 * So the name is no longer trusted on its own:
 *
 *   1. the MIME type of the bytes, when it is a known image type;
 *   2. otherwise the name's extension, when it is on the allowlist;
 *   3. otherwise `jpg`.
 *
 * The bytes come first because they are the truth: a resized PNG is JPEG bytes,
 * and a URL can end in anything. The allowlist — rather than "a few
 * alphanumeric characters", which is what the more careful uploaders checked —
 * is what stops `image` passing as an extension.
 *
 * Pure, and free of Firebase, so it can be tested on its own. The repair for
 * the objects already stored under a wrong name lives in
 * `functions/src/repairCalibreImageNamesPlan.ts`, which carries the same table.
 */

/** Image MIME type → the extension it is stored under. */
const EXTENSION_BY_MIME: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/pjpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/avif': 'avif',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/bmp': 'bmp',
  'image/tiff': 'tiff',
  'image/svg+xml': 'svg',
};

/** Extensions accepted off a file name → the extension stored. */
const EXTENSION_BY_NAME: Record<string, string> = {
  jpg: 'jpg',
  jpeg: 'jpg',
  png: 'png',
  webp: 'webp',
  gif: 'gif',
  avif: 'avif',
  heic: 'heic',
  heif: 'heif',
  bmp: 'bmp',
  tif: 'tiff',
  tiff: 'tiff',
  svg: 'svg',
};

const MIME_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  avif: 'image/avif',
  heic: 'image/heic',
  heif: 'image/heif',
  bmp: 'image/bmp',
  tiff: 'image/tiff',
  svg: 'image/svg+xml',
};

/** The extension a MIME type maps to, or undefined when it is not a known image type. */
export function extensionForImageMime(mime: string | null | undefined): string | undefined {
  if (!mime) return undefined;
  // "image/jpeg; charset=binary" and "IMAGE/JPEG" are both image/jpeg.
  return EXTENSION_BY_MIME[mime.split(';')[0].trim().toLowerCase()];
}

/**
 * The allowlisted extension a file name or URL path ends in, or undefined.
 * A query string or fragment is ignored, and a name with no dot has none.
 */
export function extensionFromImageName(name: string | null | undefined): string | undefined {
  if (!name) return undefined;
  const base = name.split(/[?#]/)[0].split('/').pop() ?? '';
  const dot = base.lastIndexOf('.');
  if (dot < 0) return undefined;
  return EXTENSION_BY_NAME[base.slice(dot + 1).toLowerCase()];
}

/**
 * The extension to store `file` under. Always one of the allowlisted image
 * extensions, lower case, never empty.
 *
 * @param file     the bytes being uploaded (only `type` is read)
 * @param fileName the name they came with, if any: a picked file's name, or the
 *                 last segment of the URL they were fetched from
 */
export function imageFileExtension(
  file: { type?: string | null } | null | undefined,
  fileName?: string | null,
): string {
  return extensionForImageMime(file?.type) ?? extensionFromImageName(fileName) ?? 'jpg';
}

/**
 * The content type to upload with: the bytes' own when they state an image
 * type, otherwise the one implied by the extension chosen for them.
 */
export function imageContentType(
  file: { type?: string | null } | null | undefined,
  extension: string,
): string {
  const stated = file?.type?.split(';')[0].trim().toLowerCase();
  if (stated && stated.startsWith('image/')) return stated;
  return MIME_BY_EXTENSION[extension] ?? 'image/jpeg';
}
